import fieldImage from "@/assets/preview-hero.jpg";
import constructionImage from "@/assets/kori-img1.jpg";
import koriInlineImage from "@/assets/kori-img2.jpg";
import eventImage from "@/assets/crowdguard-hvm-street.webp";
import jamesHarrisAsset from "@/assets/new-site/james-harris-board.jpg.asset.json";
import jamesHarrisPortraitAsset from "@/assets/new-site/james-harris-portrait.jpg.asset.json";
import previsicoNetworkRailAsset from "@/assets/new-site/previsico-network-rail-culvert.webp.asset.json";
import previsicoFloodAsset from "@/assets/new-site/previsico-flood-culvert.jpg.asset.json";
import crowdguardHeroAsset from "@/assets/new-site/case-studies-hero-crowdguard.jpg.asset.json";
import juliaTimmsAsset from "@/assets/new-site/team-julia-timms-card.jpg.asset.json";
import streetsAheadAsset from "@/assets/new-site/snapshot-live-streets-ahead-stand.jpg.asset.json";
import vinciEventAsset from "@/assets/new-site/vinci-inspiration-event.jpg.asset.json";
import dockLeicesterAsset from "@/assets/new-site/snapshot-live-dock-leicester.webp.asset.json";
const jamesHarrisImage = jamesHarrisAsset.url;
const crowdguardHeroImage = crowdguardHeroAsset.url;

export const topics = ["All Stories", "Resilience", "Streetworks", "Construction", "Product Updates", "Company News"] as const;
export type Topic = typeof topics[number];
export interface Story {
  id: string;
  date: string;
  category: string;
  topic: Topic;
  title: string;
  summary: string;
  image: string;
  imageAlt: string;
  /** Optional full-article content; add when the real text is supplied. */
  lead?: string;
  inlineImage?: string;
  /** Description for the in-text photo when it differs from the card photo. */
  inlineImageAlt?: string;
  /** Caption shown under the in-text photo; set to "" to show none. */
  caption?: string;
  /** Extra classes for the in-text photo, e.g. to move which part of it is visible. */
  inlineImageClass?: string;
  /** Optional taller banner for photos whose subject needs more height than the default crop. */
  headerClass?: string;
  /** Set true to keep an article out of the News list and its own page until the work it describes is real. */
  hidden?: boolean;
  sections?: { heading?: string; paragraphs: string[] }[];
}

// Editorial preview copy supplied by Figma; full articles and final image exports are pending.

export const stories: Story[] = [
  {
    id: "previsico-flood-intelligence-partnership",
    date: "June 2026",
    category: "Resilience",
    topic: "Resilience",
    title: "Snapshot Live and Previsico Partner to Bring Visual Intelligence to Flood Risk Management",
    summary: "Combining real-time flood monitoring with autonomous visual intelligence to help organisations anticipate risks, make better decisions and take preventative action.",
    image: previsicoNetworkRailAsset.url,
    imageAlt: "A culvert monitored by Snapshot Live for flood risk management",
    inlineImage: previsicoFloodAsset.url,
    caption: "Floodwater at a railed culvert inlet captured by Snapshot Live",
    sections: [
      {
        paragraphs: [
          "Snapshot Live has partnered with flood forecasting specialist Previsico to integrate autonomous visual monitoring into its Flood Intelligence Platform, giving organisations a clearer, more complete picture of emerging flood risks.",
          "Traditionally, flood monitoring has relied on forecasts, water-level sensors and physical site inspections. While sensors provide critical data, they cannot always reveal what is happening on the ground, such as debris accumulating around a culvert or a drainage channel becoming obstructed.",
          "By combining Previsico's real-time flood intelligence with Snapshot Live's solar-powered, off-grid cameras, customers can now see conditions at vulnerable locations remotely, without the need for frequent site visits.",
        ],
      },
      {
        heading: "From reactive inspections to proactive intervention",
        paragraphs: [
          "Snapshot Live's autonomous cameras capture and transmit images from flood-prone locations, providing time-stamped visual evidence of changing conditions.",
          "As forecast flood risk increases, image capture frequency can automatically increase, giving operational teams more frequent updates precisely when they matter most.",
          "This combined approach enables organisations to:",
          "Identify emerging risks earlier – Visually confirm rising water levels, blocked culverts and debris accumulation before conditions escalate.",
          "Make faster, better-informed decisions – Combine sensor data, forecasts and visual evidence to determine when intervention is required.",
          "Reduce unnecessary site visits – Remotely assess conditions and deploy teams only where they are needed.",
          "Move from reactive to preventative maintenance – Identify potential obstructions and take action before severe weather arrives.",
          "Improve post-event assessment – Maintain a reliable visual record to support incident reviews, damage assessments and insurance claims.",
        ],
      },
      {
        heading: "Already delivering results for National Highways",
        paragraphs: [
          "The combined technology is already supporting National Highways in managing flood risks across critical infrastructure.",
          "By bringing together sensor measurements and visual intelligence, engineering teams can identify debris accumulation around vulnerable structures and arrange preventative clearance ahead of forecast rainfall, reducing the likelihood of flooding and infrastructure disruption.",
          "As Andrew Simpson, Structures Engineer at National Highways, explains:",
          '"The sensors and cameras have allowed us to carry out preventative debris clearance from our structures and reduced the risk of future flooding significantly."',
        ],
      },
      {
        heading: "A smarter approach to flood resilience",
        paragraphs: [
          "The partnership demonstrates how combining environmental data with autonomous visual intelligence can transform the management of critical infrastructure.",
          "Rather than relying solely on sensor alerts or scheduled inspections, organisations gain the ability to understand what is happening, assess the level of risk and act before minor issues become major incidents.",
          "By turning flood intelligence into actionable visual insight, Snapshot Live and Previsico are helping organisations move from responding to flooding to actively preventing its impact.",
        ],
      },
    ],
  },
  {
    id: "streets-ahead-birmingham",
    date: "June 2026",
    category: "Events",
    topic: "Company News",
    title: "Snapshot Live at Streets Ahead",
    summary: "Matt Bright represented Snapshot Live in Birmingham at HTM's Streets Ahead event, showcasing innovative solutions for traffic management.",
    image: streetsAheadAsset.url,
    imageAlt: "A Snapshot Live camera mounted on a display stand at an exhibition, with a banner showing field workers behind it",
    headerClass: "h-[220px] w-full object-cover md:h-auto md:aspect-[8/5]",
    inlineImageClass: "object-[50%_42%]",
    caption: "The Snapshot Live camera on display at HTM's Streets Ahead in Birmingham",
    lead: "Snapshot Live exhibited in Birmingham at HTM's Streets Ahead, with Strategic Growth Manager Matt Bright representing the business and showcasing innovative solutions for traffic management.",
    sections: [
      {
        paragraphs: [
          "Snapshot Live exhibited its autonomous visual intelligence solution, showing how solar-powered, off-grid cameras capture time-stamped images of a location without fixed power or connectivity.",
          "For temporary traffic management and streetworks operations, that means conditions at a site can be reviewed remotely and at any point in time, without sending someone to look. Scheduled images provide a visual record of a scheme, while privacy blurring and AI-assisted review can highlight changes that need attention.",
        ],
      },
      {
        heading: "Continuing the conversation",
        paragraphs: [
          "The event gave Snapshot Live the opportunity to discuss with traffic management and highway operators how visual evidence supports day-to-day decisions, reduces unnecessary site visits and provides assurance to stakeholders and the public.",
        ],
      },
    ],
  },
  {
    id: "vinci-innovation-inspiration-event",
    date: "June 2026",
    category: "Events",
    topic: "Company News",
    title: "Snapshot Live at Vinci's Innovation Inspiration Event",
    summary: "Snapshot Live attended Vinci's Innovation Inspiration Event on 24 June 2026, showcasing autonomous visual monitoring to Vinci, Ringway and their clients, with several trials now in progress.",
    image: vinciEventAsset.url,
    imageAlt: "Vinci Construction and Innovation Inspiration flags outside the event venue, with Snapshot Live cameras displayed on a scissor lift",
    caption: "Snapshot Live cameras on display at Vinci's Innovation Inspiration Event",
    lead: "Snapshot Live attended Vinci's Innovation Inspiration Event on 24 June 2026, an event that brought together all of Vinci's business units, including Ringway, alongside their clients to look at future innovation.",
    sections: [
      {
        paragraphs: [
          "The event brought together Vinci's business units, including Ringway, and their clients to explore future innovation across the group. Snapshot Live was delighted to be present, demonstrating how its UK designed and manufactured Snapshot cameras deliver time-stamped visual intelligence from almost any location, without fixed power, connectivity or infrastructure.",
          "On the stand, visitors could see how scheduled still images and AI-assisted review give operational teams remote visibility of their sites, supporting streetworks, construction, resilience and facilities operations with evidence they can review at any point in time.",
        ],
      },
      {
        heading: "Engaging with Vinci and Ringway teams",
        paragraphs: [
          "Through the day, the Snapshot Live team engaged with Vinci and Ringway teams from across the business, discussing how autonomous image-based monitoring can support highways, construction and asset operations, from temporary works and site security through to longer-term visibility of critical infrastructure.",
        ],
      },
      {
        heading: "Trials now in progress",
        paragraphs: [
          "Following the event, Snapshot Live is now working on several trials of Snapshot to deliver operational benefits and safety improvements. Each trial will show how scheduled images and AI-assisted review can reduce unnecessary site visits, highlight changes that need attention and provide evidence-based assurance to stakeholders and clients.",
        ],
      },
    ],
  },
  {
    id: "julia-timms-board-matthew-timms-advisor",
    date: "May 2026",
    category: "Company News",
    topic: "Company News",
    title: "Snapshot Live Welcomes Julia Timms to the Board",
    summary: "Julia Timms joins as Non-Executive Director, bringing commercial and operational leadership across technology, infrastructure and FDI-driven markets.",
    image: juliaTimmsAsset.url,
    imageAlt: "Julia Timms, Non-Executive Director at Snapshot Live",
    headerClass: "h-[220px] w-full object-cover object-top md:h-auto md:aspect-[4/3]",
    sections: [
      {
        paragraphs: [
          "Snapshot Live is pleased to announce that Julia Timms has joined its Board as a Non-Executive Director, the appointment made in May 2026.",
          "Julia supports high-growth businesses across technology, real estate, infrastructure and private office and FDI-driven markets. She is currently COO at Dignified Life, a premium advisory start-up serving high-net-worth individuals through major life transitions, where she is building the operational and commercial platform from the ground up.",
          "She was previously Divisional Managing Director and Chief Commercial Officer at IFZA, the UAE's leading free zone, where she designed and launched five new business verticals including the group's first AI innovation accelerator, and has held C-suite roles at Sidara, GHD and LSE-listed Air Partner PLC across global markets. She is a passionate advocate for high-growth businesses that build platform-led commercial models to achieve durable, capital-efficient scale.",
        ],
      },
      {
        paragraphs: [
          "Julia's appointment further strengthens Snapshot Live's leadership as it works to make remote infrastructure monitoring smarter, more accessible and more cost-effective through autonomous, AI-powered visual intelligence.",
        ],
      },
    ],
  },
  {
    id: "james-harris-board",
    date: "January 2026",
    category: "Company News",
    topic: "Company News",
    title: "Snapshot Live Welcomes James Harris to the Board",
    summary: "Former one.network founder and CEO brings extensive experience in scaling technology businesses and transforming infrastructure markets.",
    image: jamesHarrisImage,
    imageAlt: "A stack of Snapshot Live product boxes on a conference table, two colleagues talking behind",
    inlineImage: jamesHarrisPortraitAsset.url,
    inlineImageAlt: "James Harris, arms folded, wearing a grey blazer over an open-collared check shirt against a dark studio backdrop",
    caption: "",
    inlineImageClass: "object-[50%_10%]",
    headerClass: "h-[220px] w-full object-cover md:h-auto md:aspect-[16/9] md:object-bottom",
    sections: [
      {
        paragraphs: [
          "Snapshot Live is pleased to announce that James Harris has joined its Board as a Non-Executive Director, bringing a wealth of experience in building and scaling successful technology businesses across the infrastructure sector.",
          "James was previously the founder and CEO of one.network, a private equity-backed SaaS business that became a market leader in traffic management technology across the UK and US before being acquired in 2023.",
          "Today, James supports ambitious AI-native technology startups as a Non-Executive Director and Chair, helping businesses translate innovation into meaningful commercial growth.",
          "His appointment comes at an important stage in Snapshot Live's journey as the company expands its autonomous visual intelligence solutions across infrastructure monitoring, flood resilience, streetworks and temporary traffic management.",
        ],
      },
      {
        heading: "James Harris commented",
        paragraphs: [
          "\u201CI'm excited to be joining Snapshot Live at such an important stage in its development. The business has a compelling proposition, combining practical, scalable technology with AI to solve real challenges across infrastructure. I've seen first-hand how digital transformation can fundamentally change established markets, and I believe Snapshot Live has a significant opportunity to do just that. I'm looking forward to supporting the team as they scale.\u201D",
        ],
      },
      {
        heading: "Roger Poeth, Founder and CEO of Snapshot Live, added",
        paragraphs: [
          "\u201CWe're delighted to welcome James to the Board. His experience in building one.network into a market-leading business across the UK and US is hugely relevant to our ambitions. James understands both the infrastructure sector and what it takes to scale a technology company successfully. His strategic insight, industry knowledge and commercial experience will be invaluable as we enter our next phase of growth.\u201D",
        ],
      },
      {
        paragraphs: [
          "James's appointment further strengthens Snapshot Live's leadership as it works to make remote infrastructure monitoring smarter, more accessible and more cost-effective through autonomous, AI-powered visual intelligence.",
        ],
      },
    ],
  },
  {
    id: "dock-leicester-space-city",
    date: "September 2026",
    category: "Company News",
    topic: "Company News",
    title: "Snapshot Live Moves into Dock Leicester in Space City to Support Expansion",
    summary: "Snapshot Live has moved into the Dock at Space City in Leicester, giving the growing team the room and infrastructure to support its next phase of expansion.",
    image: dockLeicesterAsset.url,
    imageAlt: "The Dock at Space City in Leicester, modern workspace buildings with dark ribbed cladding and orange doors beside a wet car park on an overcast day",
    caption: "The Dock at Space City, Leicester",
    lead: "Snapshot Live has moved into the Dock at Space City in Leicester to support the expansion of its autonomous visual intelligence business.",
    sections: [
      {
        paragraphs: [
          "Snapshot Live has taken up space at the Dock, the managed workspace at the heart of Space City, the space and Earth observation campus north of Leicester city centre. The move gives the business the room and infrastructure it needs as it enters its next phase of growth.",
          "Space City brings together the University of Leicester's Space Park research campus, high-tech business and industrial workspaces, and the National Space Centre, forming part of the UK's 'Space Spine' of space-related industry.",
        ],
      },
      {
        heading: "Room to grow",
        paragraphs: [
          "The Dock offers serviced offices, meeting rooms and light industrial units for high-tech, low-carbon and innovative businesses. For Snapshot Live, that means the team, operations and customer activity can expand from a single base without the disruption of moving again as the company scales.",
          "Being part of a community of space, Earth observation and data businesses also opens up opportunities to collaborate, share expertise and recruit people with the skills the company needs next.",
        ],
      },
      {
        heading: "A base for what's next",
        paragraphs: [
          "The move supports continued expansion across infrastructure monitoring, flood resilience, streetworks and temporary traffic management, and keeps Snapshot Live's design, build and customer support work in Leicester, where its cameras are designed.",
        ],
      },
    ],
  },
  { id: "cyber-essentials", hidden: true, date: "Sep 28, 2026", category: "Company", topic: "Company News", title: "Snapshot Live Achieves Cyber Essentials Plus", summary: "Securing remote visual assets is paramount. We have obtained certified compliance to ensure robust military-grade data protection.", image: fieldImage, imageAlt: "Snapshot Live camera operating in the field" },
  {
    id: "kori-ai-safety-rollout",
    date: "April 2026",
    category: "Construction",
    topic: "Construction",
    title: "Kori to roll out Snapshot Live to Their Construction Sites",
    summary: "Portable, AI-assisted cameras give Kori proactive, data-driven oversight of site safety and community impact across its construction sites.",
    image: constructionImage,
    imageAlt: "Kori construction site access point monitored by a Snapshot Live camera",
    inlineImage: koriInlineImage,
    caption: "Site access point monitored by Snapshot Live",
    lead: "Kori Construction is rolling out Snapshot Live's AI-assisted visual monitoring across its sites, bringing privacy-protected still images and automated safety review to complex urban construction environments.",
    sections: [
      {
        heading: "The Challenge",
        paragraphs: [
          "Kori Construction is a leading UK-based construction firm specialising in premium residential care homes. Kori sought to enhance visibility and control across multiple construction sites, particularly in complex urban environments.",
          "Key challenges included managing site access points which directly impact local communities, ensuring minimal disruption on busy public roads, maintaining high safety standards across multiple trades in confined environments, and providing evidence-based assurance to stakeholders and community groups.",
        ],
      },
      {
        heading: "Monitoring without fixed infrastructure",
        paragraphs: [
          "Snapshot Live deployed its AI-enabled camera system across Kori's sites, starting with a project in Highgate, London. Cameras were positioned at critical access points capturing privacy-protected still images throughout the day, enabling continuous monitoring of road cleanliness, traffic interaction, and site interface with the public.",
          "In parallel, Snapshot Live and Kori developed a portable camera stand, enabling daily repositioning of cameras across the site based on work activity and flexible monitoring of high-risk zones without fixed infrastructure.",
        ],
      },
      {
        heading: "AI that follows the work",
        paragraphs: [
          "An advanced AI query layer was implemented to automatically identify safety risks including slip, trip and fall hazards, PPE compliance, unsafe interactions between pedestrians and plant, ladder use and general site safety observations.",
          "This allowed site managers to simply move the camera, while the AI continuously assessed risk in each new location.",
        ],
      },
      {
        heading: "The Result",
        paragraphs: [
          "Early identification of road contamination led to rapid operational changes, alongside enhanced cleaning processes and better management of pedestrian barriers. Site teams can now identify and act on hazards before escalation, while time-stamped evidence provides demonstrable assurance to stakeholders and community groups.",
          "Snapshot Live enabled Kori to move from reactive site safety management to proactive, data-driven oversight.",
        ],
      },
      {
        heading: "James Jarrett, Snapshot Live, commented",
        paragraphs: [
          "\u201CKori's approach to innovation and continuous improvement makes them an ideal partner for Snapshot Live. By combining flexible deployment with AI-driven analysis, we're enabling their teams to gain real-time insight into site operations, improve safety outcomes, and provide transparent assurance to stakeholders and local communities.\u201D",
        ],
      },
    ],
  },
  {
    id: "crowdguard",
    date: "July 2026",
    category: "Company News",
    topic: "Company News",
    title: "Snapshot Live Strengthens HVM Installation Assurance with Crowdguard",
    summary: "Snapshot Live has partnered with Crowdguard to bring scheduled, time-stamped images and AI-assisted review to Hostile Vehicle Mitigation deployments, helping verify that units are present and correctly positioned, with notifications shared with the client, Crowdguard and installation supervisors.",
    image: eventImage,
    imageAlt: "Crowdguard hostile vehicle mitigation street layout",
    inlineImage: crowdguardHeroImage,
    caption: "A Snapshot Live camera and CrowdGuard information sign mounted on a post in front of a brick boundary wall",
    lead: "Snapshot Live has partnered with Crowdguard to strengthen HVM installation assurance, supporting verification that units appear present and aligned with the agreed positioning at defined points in time.",
    sections: [
      {
        heading: "A partnership built on visual assurance",
        paragraphs: [
          "Snapshot Live has partnered with Crowdguard, a specialist in hostile vehicle mitigation, to add an additional layer of assurance to HVM deployments. Scheduled, time-stamped images and AI-assisted review give organisations a clear visual record of HVM placement and configuration across the life of an operation.",
          "Scheduled, time-stamped still images create a clear visual reference, and where AI-assisted review identifies potential movement or misalignment, notifications can be shared with the client, Crowdguard, and installation supervisors to enable a prompt, targeted check.",
        ],
      },
      {
        paragraphs: [
          "This supports timely corrective action where needed, helps improve consistency of installation standards, and can inform post-event review and continuous improvement – including identifying where additional installation training may be beneficial.",
        ],
      },
      {
        heading: "Roger Poeth, Founder and CEO of Snapshot Live, commented",
        paragraphs: [
          "\u201CWe are proud to partner with Crowdguard to add an additional layer of assurance to HVM deployments. By combining Crowdguard's expertise in protective infrastructure with Snapshot Live's automated visual monitoring, organisations gain clear, time-stamped oversight of HVM placement and configuration. The result is improved quality assurance, faster issue detection, and a reliable visual record that supports safer events and public spaces.\u201D",
        ],
      },
      {
        heading: "Deborah Ainscough, Founder of Crowdguard, added",
        paragraphs: [
          "\u201CFor safety officers, assurance matters. Snapshot Live helps support that by providing a practical, non-intrusive way to evidence that agreed HVM measures appear to be in place at key stages of a live operation. It is about strengthening visibility, accountability and confidence in the matchday environment.\u201D",
        ],
      },
    ],
  },
];

/** Articles are listed newest first; "June 2026" style dates sort as the first of that month. */
export const storyDateValue = (date: string) => {
  const parsed = Date.parse(date);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const newestFirst = [...stories].filter((s) => !s.hidden).sort((a, b) => storyDateValue(b.date) - storyDateValue(a.date));

/** The flagship partnership article leads the News page; the grid below shows everything else. */
export const featuredStory: Story = newestFirst.find((s) => s.id === "previsico-flood-intelligence-partnership") ?? newestFirst[0];

/** The featured article is shown on its own, so the grid lists the rest, newest first. */
export const storiesByDate: Story[] = newestFirst.filter((s) => s.id !== featuredStory.id);

/** Hidden articles are not listed and their direct address falls back to the News page. */
export const findStory = (id?: string) => stories.find((s) => s.id === id && !s.hidden);
